import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const US_CENTER = { lat: 39.5, lng: -98.35 };

async function geocodeQuery(query) {
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: query }, (results, status) => {
      const first = results?.[0];

      if (status === "OK" && first?.geometry?.location) {
        const loc = first.geometry.location;

        resolve({
          center: { lat: loc.lat(), lng: loc.lng() },
          viewport: first.geometry.viewport || null,
          bounds: first.geometry.bounds || null,
        });
      } else {
        resolve(null);
      }
    });
  });
}

const COUNTY_TARGET_ZOOM = 11;
const STATE_INSIDE_ZOOM = 7;

async function resolveAgentCenter(access) {
  if (!access?.length) return { center: US_CENTER, zoom: 4, viewport: null };

  const county = access[0].county_name;
  const stateCode = access[0].state_code || access[0].state;

  if (county && stateCode) {
    const result = await geocodeQuery(`${county} County, ${stateCode}, USA`);
    // Prefer bounds (precise admin-area bbox) over viewport (looser display rect).
    if (result) return { center: result.center, zoom: COUNTY_TARGET_ZOOM, viewport: result.bounds || result.viewport };
  }

  if (stateCode) {
    const result = await geocodeQuery(`${stateCode}, USA`);
    if (result) return { center: result.center, zoom: STATE_INSIDE_ZOOM, viewport: result.bounds || result.viewport };
  }

  return { center: US_CENTER, zoom: 4, viewport: null };
}

function circleToGeoJSON(circle) {
  const center = circle.getCenter();
  const radius = circle.getRadius();

  const lat = center.lat();
  const lng = center.lng();

  const coords = [];

  for (let i = 0; i <= 64; i++) {
    const angle = (i * 2 * Math.PI) / 64;

    const dLat =
      (radius / 111320) * Math.cos(angle);

    const dLng =
      (radius /
        (111320 *
          Math.cos((lat * Math.PI) / 180))) *
      Math.sin(angle);

    coords.push([lng + dLng, lat + dLat]);
  }

  return {
    type: "Polygon",
    coordinates: [coords],
  };
}

function polygonToGeoJSON(polygon) {
  const path = polygon.getPath().getArray();

  const coords = path.map((p) => [p.lng(), p.lat()]);

  coords.push(coords[0]);

  return {
    type: "Polygon",
    coordinates: [coords],
  };
}

export default function MapDrawingFilter({
  onPolygonChange,
  access,
}) {
  const mapRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const shapeRef = useRef(null);

  const [hasShape, setHasShape] = useState(false);

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;

      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }

      const dm =
        new window.google.maps.drawing.DrawingManager({
          drawingMode: null,

          drawingControl: true,

          drawingControlOptions: {
            position:
              window.google.maps.ControlPosition
                .TOP_CENTER,

            drawingModes: [
              window.google.maps.drawing.OverlayType
                .POLYGON,

              window.google.maps.drawing.OverlayType
                .CIRCLE,
            ],
          },

          polygonOptions: {
            fillColor: "#3b82f6",
            fillOpacity: 0.2,
            strokeColor: "#1d4ed8",
            strokeWeight: 2,
            editable: true,
          },

          circleOptions: {
            fillColor: "#3b82f6",
            fillOpacity: 0.2,
            strokeColor: "#1d4ed8",
            strokeWeight: 2,
            editable: true,
          },
        });

      dm.setMap(map);

      drawingManagerRef.current = dm;

      window.google.maps.event.addListener(
        dm,
        "overlaycomplete",
        (e) => {
          if (shapeRef.current) {
            shapeRef.current.setMap(null);
          }

          shapeRef.current = e.overlay;

          setHasShape(true);

          dm.setDrawingMode(null);

          const geoJson =
            e.type ===
            window.google.maps.drawing.OverlayType
              .CIRCLE
              ? circleToGeoJSON(e.overlay)
              : polygonToGeoJSON(e.overlay);

          onPolygonChange(JSON.stringify(geoJson));
        }
      );
    },
    [onPolygonChange]
  );

  useEffect(() => {
    return () => {
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }

      if (shapeRef.current) {
        shapeRef.current.setMap(null);
        shapeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !access?.length) return;

    const isAlive = { current: true };

    resolveAgentCenter(access).then(({ center, zoom, viewport }) => {
        requestAnimationFrame(() => {
          const map = mapRef.current;
          if (!map || !isAlive.current) return;

          if (viewport) {
            // Fit exactly to the county/state boundary returned by the geocoder
            map.fitBounds(viewport);
          } else {
            map.setCenter(center);
            map.setZoom(zoom);
          }
        });
      }
    );

    return () => { isAlive.current = false; };
  }, [access]);

  const clearShape = useCallback(() => {
    if (shapeRef.current) {
      shapeRef.current.setMap(null);
      shapeRef.current = null;
    }

    setHasShape(false);

    onPolygonChange("");
  }, [onPolygonChange]);

  return (
    <div className="relative h-full w-full">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        defaultCenter={US_CENTER}
        defaultZoom={4}
        onLoad={onMapLoad}
        options={{
          streetViewControl: true, // little yellow person
          mapTypeControl: true, // Map / Satellite toggle
          fullscreenControl: true,
          zoomControl: true,

          mapTypeControlOptions: {
            style:
              window.google.maps.MapTypeControlStyle
                .HORIZONTAL_BAR,
            position:
              window.google.maps.ControlPosition
                .TOP_RIGHT,
          },

          streetViewControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_BOTTOM,
          },

          fullscreenControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_TOP,
          },

          zoomControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_CENTER,
          },
        }}
      />

      {hasShape && (
        <button
          type="button"
          onClick={clearShape}
          className="absolute top-3 left-3 z-10 rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 shadow-md transition hover:border-zinc-900 hover:text-zinc-950"
        >
          Clear Shape
        </button>
      )}
    </div>
  );
}